import { FC, useMemo } from "react"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { LoadingIndicator } from "components/general/LoadingIndicator"

import { Tenant } from "types/api"

import { PROVIDER_FIELD_GROUPS } from "./live-chat/fields"
import { buildTenantOrigins, PROVIDER_GUIDES } from "./live-chat/guides"
import { PlacementSection } from "./live-chat/PlacementSection"
import { ProviderCredentialsSection } from "./live-chat/ProviderCredentialsSection"
import { ProviderGuideSection } from "./live-chat/ProviderGuideSection"
import { ProviderSection } from "./live-chat/ProviderSection"
import { useSettings } from "./live-chat/useSettings"
import { hasErrors, reliesOnEnvironment, validateForm } from "./live-chat/validation"
import { ResetToDefaultButton } from "./ResetToDefaultButton"
import { SettingsHistoryNote } from "./SettingsHistoryNote"

interface LiveChatTabProps {
  /** Source of the origins a vendor has to allowlist for this brand. */
  tenant: Tenant | null
}

export const LiveChatTab: FC<LiveChatTabProps> = ({ tenant }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { form, loaded, loading, saving, setField, handleSave, refetch } = useSettings()

  const errors = useMemo(() => validateForm(form), [form])
  const origins = useMemo(() => buildTenantOrigins(tenant), [tenant])

  // Exactly one widget can be live, so only the selected provider is configurable.
  const selectedGroup = PROVIDER_FIELD_GROUPS.find((group) => group.provider === form.provider)
  const selectedGuide = PROVIDER_GUIDES.find((guide) => guide.provider === form.provider)

  return (
    <section className="flex flex-col gap-6 mt-6">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.liveChat.sectionTitle")}</h3>
        <SettingsHistoryNote target="liveChat" />
      </div>

      {loading ? (
        <LoadingIndicator text={t("settings.liveChat.loading") as string} />
      ) : (
        <>
          <ProviderSection form={form} saving={saving} onFieldChange={setField} />

          {selectedGuide && <ProviderGuideSection guide={selectedGuide} origins={origins} />}

          {selectedGroup && (
            <ProviderCredentialsSection
              group={selectedGroup}
              form={form}
              loaded={loaded}
              errors={errors}
              saving={saving}
              showEnvNotice={reliesOnEnvironment(form)}
              onFieldChange={setField}
            />
          )}

          <PlacementSection form={form} errors={errors} saving={saving} onFieldChange={setField} />

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <ResetToDefaultButton
              target="liveChat"
              label={t("settings.tabs.liveChat")}
              onDone={refetch}
              className="btn-outline btn-sm w-full sm:w-auto"
            />
            <SaveButton
              onClick={handleSave}
              loading={saving}
              disabled={hasErrors(errors)}
              className="!w-full sm:!w-auto sm:!min-w-[12rem] !mr-0 !self-auto"
            />
          </div>
        </>
      )}
    </section>
  )
}
